import { createApi, type TokenStore } from "@app/api-client";

/**
 * API client。
 *
 * base URL 預設是**相對路徑** `/api/v1`：
 *   - 開發時由 vite 的 proxy 轉到 http://localhost:8000（見 vite.config.ts）
 *   - 生產時由 nginx 同源提供（見 infra/nginx/nginx.conf）
 * 兩邊行為一致，所以不需要 CORS，也不需要在前端維護 API 的絕對網址。
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

const REFRESH_STORAGE_KEY = "auth.refresh";

/**
 * access token 只放記憶體 —— 它十分鐘就過期，而且放進 localStorage 會擴大
 * XSS 的影響範圍。refresh token 放 localStorage 是為了重新整理後不必重新登入；
 * 更嚴格的做法是讓後端改用 httpOnly cookie 承載 refresh token。
 */
let accessToken: string | null = null;

export const tokenStore: TokenStore = {
  getAccess: () => accessToken,

  getRefresh: () => {
    try {
      return localStorage.getItem(REFRESH_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  setTokens: ({ access, refresh }) => {
    accessToken = access;
    try {
      localStorage.setItem(REFRESH_STORAGE_KEY, refresh);
    } catch {
      // 私密瀏覽或封鎖網站資料時會拋錯，此時退化為「關掉分頁就要重新登入」
    }
  },

  clear: () => {
    accessToken = null;
    try {
      localStorage.removeItem(REFRESH_STORAGE_KEY);
    } catch {
      // 同上
    }
  },
};

/** refresh 也失敗時觸發。由 AuthProvider 註冊，導向登入頁。 */
let onSessionExpired: () => void = () => {};

export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

export const api = createApi({
  baseUrl: BASE_URL,
  tokens: tokenStore,
  onUnauthorized: () => onSessionExpired(),
});

/** 後端統一的錯誤格式（見 apps/api/common/exceptions.py）。 */
interface ApiErrorBody {
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

/**
 * API 錯誤。
 *
 * openapi-fetch 的 `error` 是後端回的 JSON 物件而不是 Error 實例，直接
 * `throw` 它會少了堆疊、也不符合「只丟 Error」的慣例。統一包成這個類別，
 * React Query 的 error 就一定是 Error，且仍保有 code 與逐欄位錯誤。
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** 逐欄位的驗證錯誤，用來標記表單欄位 */
  readonly fieldErrors: Record<string, string>;

  constructor(body: unknown, status: number, fallback = "發生未預期的錯誤。") {
    const parsed: ApiErrorBody = body && typeof body === "object" ? body : {};
    super(parsed.detail ?? fallback);
    this.name = "ApiError";
    this.status = status;
    this.code = parsed.code ?? "error";
    this.fieldErrors = Object.fromEntries(
      Object.entries(parsed.errors ?? {}).map(([field, messages]) => [field, messages[0] ?? ""]),
    );
  }
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * 把 openapi-fetch 的 `{ data, error }` 轉成「成功回傳 data、失敗丟 ApiError」。
 *
 * React Query 的 queryFn / mutationFn 都用它包一層，錯誤處理就只有一種形狀：
 *
 *   queryFn: async () => unwrap(await api.GET("/master/items/"))
 *
 * 注意：204 / 205 這類沒有 body 的回應不要用它，直接檢查 error 即可。
 */
export function unwrap<T>(result: FetchResult<T>): T {
  if (result.error !== undefined || result.data === undefined) {
    throw new ApiError(result.error, result.response.status);
  }
  return result.data;
}

/**
 * 給 204 / 205 這類沒有 body 的回應用（例如 DELETE）。
 *
 * 不能沿用 `unwrap`：它把「沒有 data」當成錯誤，204 本來就沒有 data。
 * 也不能在呼叫端直接判斷 `result.error`——openapi-fetch 的型別是逐一操作
 * 推導的，當 schema 沒有替某個操作宣告任何非 2xx 回應時（DRF 預設產生的
 * destroy 就是這樣，只宣告了 204），`error` 的型別會被推成 `never`。
 * TypeScript 因此判定 `if (result.error)` 這個分支不可能成立、整段標成
 * 不可達，區塊裡的 `result.error`／`result.response` 都變成「在 never
 * 型別上不存在」的編譯錯誤——即使執行期真的会回 403／404。
 *
 * 這裡用跟 `FetchResult` 一樣鬆散的參數型別繞開那個推導，呼叫端只要
 * `await unwrapEmpty(await api.DELETE(...))` 就好。
 */
export function unwrapEmpty(result: { error?: unknown; response: Response }): void {
  if (result.error !== undefined) {
    throw new ApiError(result.error, result.response.status);
  }
}

/** 從任何錯誤取出可以直接顯示給使用者的訊息。 */
export function errorMessage(error: unknown, fallback = "發生未預期的錯誤。"): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as ApiErrorBody).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

/** 取出逐欄位的驗證錯誤。 */
export function fieldErrors(error: unknown): Record<string, string> {
  return error instanceof ApiError ? error.fieldErrors : {};
}
